import { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { 
    collection, 
    query, 
    orderBy, 
    limit, 
    onSnapshot, 
    writeBatch, 
    doc, 
    updateDoc, 
    where,   
    getDocs,
    deleteDoc // Importante para borrar las normales
} from 'firebase/firestore';

const useNotifications = (userProfile) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userProfile) {
            setNotifications([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        // 1. Notificaciones Personales (Firestore Realtime)
        const notifsRef = collection(db, `users/${userProfile.uid}/notifications`);
        const qNotifs = query(notifsRef, orderBy('lastUpdatedAt', 'desc'), limit(50));

        // 2. Anuncios del Sistema (Firestore Fetch Una vez)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const systemRef = collection(db, 'system_announcements');
        const qSystem = query(
            systemRef, 
            where('createdAt', '>=', sevenDaysAgo), 
            orderBy('createdAt', 'desc'), 
            limit(5)
        );

        // --- FUNCIÓN DE MEZCLA ---
        const mergeData = (userNotifs, sysAnnouncements) => {
            // A. LEER LOCALSTORAGE: Recuperar lista negra local
            // Esto busca en el navegador del usuario: "¿Qué anuncios borré ya?"
            const hiddenSystemIds = JSON.parse(localStorage.getItem('hidden_system_notifs') || '[]');

            // B. FILTRAR: Solo dejamos pasar los anuncios que NO están en la lista negra
            const activeSystem = sysAnnouncements.filter(ann => !hiddenSystemIds.includes(ann.id));

            // C. FORMATEAR: Convertir anuncios a formato visual de notificación
            const formattedSystem = activeSystem.map(ann => ({
                id: ann.id,
                type: 'system',
                isRead: true, // Siempre leídos para no molestar con badge
                lastUpdatedAt: ann.createdAt, 
                previewText: ann.message,
                targetId: null,
                actorCount: 0,
                actors: []
            }));

            // D. UNIR Y ORDENAR
            const combined = [...userNotifs, ...formattedSystem];
            
            combined.sort((a, b) => {
                const dateA = a.lastUpdatedAt?.toMillis ? a.lastUpdatedAt.toMillis() : new Date(a.lastUpdatedAt).getTime();
                const dateB = b.lastUpdatedAt?.toMillis ? b.lastUpdatedAt.toMillis() : new Date(b.lastUpdatedAt).getTime();
                return dateB - dateA;
            });

            return combined;
        };

        const fetchData = async () => {
            let systemDocs = [];
            try {
                const sysSnap = await getDocs(qSystem);
                systemDocs = sysSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            } catch (err) {
                console.error("Error loading system announcements:", err);
            }

            const unsubscribe = onSnapshot(qNotifs, (snapshot) => {
                const userNotifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Mezclamos usando la función inteligente de arriba
                const finalFeed = mergeData(userNotifs, systemDocs);
                
                setNotifications(finalFeed);
                setUnreadCount(userNotifs.filter(n => !n.isRead).length);
                setLoading(false);
            }, (error) => {
                console.error("Error fetching notifications:", error);
                setLoading(false);
            });

            return unsubscribe;
        };

        let unsubscribeFunc;
        fetchData().then(unsub => { unsubscribeFunc = unsub; });

        return () => {
            if (unsubscribeFunc) unsubscribeFunc();
        };

    }, [userProfile]);

    // --- ELIMINAR NOTIFICACIÓN ---
    const deleteNotification = async (notificationId, type) => {
        // 1. Quitar visualmente de inmediato (Optimistic UI)
        setNotifications(prev => prev.filter(n => n.id !== notificationId));

        // 2. Si es del SISTEMA -> Guardar en LocalStorage (Navegador)
        if (type === 'system') {
            try {
                // Leemos lo que ya había borrado antes
                const hiddenIds = JSON.parse(localStorage.getItem('hidden_system_notifs') || '[]');
                
                // Si no estaba ya borrado, lo agregamos
                if (!hiddenIds.includes(notificationId)) {
                    hiddenIds.push(notificationId);
                    // Guardamos la nueva lista en el navegador
                    localStorage.setItem('hidden_system_notifs', JSON.stringify(hiddenIds));
                }
            } catch (e) {
                console.error("Error saving to local storage", e);
            }
            return; // Terminamos aquí, no tocamos Firebase
        }

        // 3. Si es NORMAL -> Borrar de Firestore (Base de datos)
        // Actualizamos contador local si no estaba leída
        const target = notifications.find(n => n.id === notificationId);
        if (target && !target.isRead) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }

        try {
            const notifRef = doc(db, `users/${userProfile.uid}/notifications`, notificationId);
            await deleteDoc(notifRef);
        } catch (error) {
            console.error("Error deleting notification:", error);
        }
    };

    // ... (markAsRead y markAllAsRead siguen igual que antes) ...
    const markAsRead = async (notificationId) => {
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
        const targetNotif = notifications.find(n => n.id === notificationId);
        if (targetNotif && targetNotif.type !== 'system' && !targetNotif.isRead) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        if (targetNotif && targetNotif.type === 'system') return;
        try {
            const notifRef = doc(db, `users/${userProfile.uid}/notifications`, notificationId);
            await updateDoc(notifRef, { isRead: true });
        } catch (error) { console.error(error); }
    };

    const markAllAsRead = async () => {
        const unreadNotifications = notifications.filter(n => !n.isRead && n.type !== 'system');
        if (unreadNotifications.length === 0) return;
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
        try {
            const batch = writeBatch(db);
            unreadNotifications.forEach(notif => {
                const ref = doc(db, `users/${userProfile.uid}/notifications`, notif.id);
                batch.update(ref, { isRead: true });
            });
            await batch.commit();
        } catch (error) { console.error(error); }
    };

    return { notifications, unreadCount, loading, markAllAsRead, markAsRead, deleteNotification };
};

export default useNotifications;